/**
 * Configuration-related TypeScript types
 * SHARED between frontend and backend
 * ABSOLUTE RULE: NO 'any' types allowed
 */

import { Timestamp } from 'firebase/firestore';
import { ServiceType, DescriptionFormat, EnhancementMode } from './order.types';

/**
 * PromptType enum - All 9 distinct prompt types
 * Used for prompt management and version control
 *
 * Note: Separate from ServiceType because:
 * - STAGING has 4 style variants (Modern, Minimalist, Classic, Custom)
 * - DESCRIPTION has 3 format variants (Caption, Description, MLS Remarks)
 */
export enum PromptType {
  DAY_TO_DUSK = 'DAY_TO_DUSK',
  ITEM_REMOVAL = 'ITEM_REMOVAL',
  STAGING_MODERN = 'STAGING_MODERN',
  STAGING_MINIMALIST = 'STAGING_MINIMALIST',
  STAGING_CLASSIC = 'STAGING_CLASSIC',
  STAGING_CUSTOM = 'STAGING_CUSTOM',
  DESCRIPTION_CAPTION = 'DESCRIPTION_CAPTION',
  DESCRIPTION_DESCRIPTION = 'DESCRIPTION_DESCRIPTION',
  DESCRIPTION_MLS_REMARKS = 'DESCRIPTION_MLS_REMARKS',
  DESTAGING = 'DESTAGING',
  COLLAGE = 'COLLAGE',
  WATERMARK = 'WATERMARK',
  ENHANCEMENT_GENERAL = 'ENHANCEMENT_GENERAL',
}

/**
 * PromptsConfig - Structure of /config/prompts document
 * Single document for fast atomic reads by Cloud Functions
 *
 * Template placeholders:
 * - ITEM_REMOVAL: {{itemDescription}} for user-specified items
 * - STAGING_CUSTOM: {{customPreset}} for user-specified style
 */
export interface PromptsConfig {
  DAY_TO_DUSK: string;
  ITEM_REMOVAL: string;
  STAGING_MODERN: string;
  STAGING_MINIMALIST: string;
  STAGING_CLASSIC: string;
  STAGING_CUSTOM: string;
  DESCRIPTION_CAPTION: string;
  DESCRIPTION_DESCRIPTION: string;
  DESCRIPTION_MLS_REMARKS: string;
  DESTAGING: string;
  COLLAGE: string;
  WATERMARK: string;
  ENHANCEMENT_GENERAL: string;
  updatedAt: Timestamp;
  updatedBy: string;
}

export interface ServiceConfig {
  serviceType: ServiceType;
  creditsCost: number;
  maxAttempts: number;
  enabled: boolean;
}

export interface PricingConfig {
  packages: PricingPackage[];
  currencyCode: string;
  currencySymbol: string;
}

export interface PricingPackage {
  id: string;
  name: string;
  credits: number;
  priceCAD: number;
  badge?: string; // e.g., "BEST VALUE", "LIMITED SPECIAL"
}

export interface AIPromptConfig {
  serviceType: ServiceType;
  descriptionFormat?: DescriptionFormat;
  enhancementMode?: EnhancementMode;
  prompt: string;
  version: number;
  createdAt: Timestamp;
  createdBy: string;
}

/**
 * PromptVersionDocument - Structure of /promptVersions collection documents
 * Immutable audit trail for all prompt changes
 *
 * Note: Uses PromptType (not ServiceType) to handle:
 * - 4 staging style variants
 * - 3 description format variants
 */
export interface PromptVersionDocument {
  promptType: PromptType;
  content: string;
  version: number;
  createdAt: Timestamp;
  createdBy: string;
}

/**
 * @deprecated Use PromptVersionDocument with PromptType instead
 * Kept for backward compatibility with existing promptVersions documents
 */
export interface LegacyPromptVersionDocument {
  promptType: ServiceType;
  descriptionFormat: DescriptionFormat | null;
  content: string;
  versionNumber: number;
  createdAt: Timestamp;
  createdBy: string;
  isActive: boolean;
}

export interface SystemConfig {
  storageConfig: StorageConfig;
  aiConfig: AIConfig;
}

export interface StorageConfig {
  maxFileSize: number; // in bytes (default 10MB)
  perUserStorageLimit: number; // in bytes (default 250MB)
  tier1Threshold: number; // 200MB (80%)
  tier2Threshold: number; // 250MB (100%)
  tier3Threshold: number; // 300MB (120%)
}

export interface AIConfig {
  imageModel: string; // gemini-2.5-flash-image
  textModel: string; // gemini-2.5-flash
  temperature: number; // 0.9
  maxOutputTokens: number; // 2048
  location: string; // us-central1
}

export interface ConfigDocument {
  services: ServiceConfig[];
  pricing: PricingConfig;
  system: SystemConfig;
  aiPrompts: AIPromptConfig[];
}
